import React, { useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchSyncStatus, triggerMTGJSONSync, type SyncStatus, type SyncServiceStatus } from '../api/mtgjson';
import { fetchChannelOperationsDashboard, type ChannelOperationsDashboard } from '../api/dashboards';
import { Button, Alert } from '../components/ui';
import { Spinner } from '../components/system/LoadingStates';
import { MtgjsonLoadStatus } from '../components/system/MtgjsonLoadStatus';
import { TcgcsvCatalogStatus } from '../components/system/TcgcsvCatalogStatus';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../styles/tokens';
import { formatRelativeTime } from '../utils/datetime';

export const DashboardsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'operations' | 'sync'>('operations');

  return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Dashboards</h1>
          <p style={styles.subtitle}>Monitor system health and operations</p>
        </div>

        {/* Tab Navigation */}
        <div style={styles.tabs}>
          <button
            onClick={() => setActiveTab('operations')}
            style={{
              ...styles.tab,
              ...(activeTab === 'operations' ? styles.tabActive : {})
            }}
          >
            Channel Operations
          </button>
          <button
            onClick={() => setActiveTab('sync')}
            style={{
              ...styles.tab,
              ...(activeTab === 'sync' ? styles.tabActive : {})
            }}
          >
            Catalog Sync Status
          </button>
        </div>

        {/* Tab Content */}
        <div style={styles.content}>
          {activeTab === 'operations' && (
            <OperationsDashboard onOpenCatalog={() => setActiveTab('sync')} />
          )}
          {activeTab === 'sync' && <SyncStatusDashboard />}
        </div>
      </div>
  );
};

interface OperationsDashboardProps {
  onOpenCatalog: () => void;
}

/** Snapshot poll while Channel Operations stays mounted. Catalog widgets are a different tab. */
const OPERATIONS_POLL_MS = 30_000;
/** Recompute relative generated age between polls (minute buckets). */
const OPERATIONS_AGE_TICK_MS = 15_000;

const OperationsDashboard: React.FC<OperationsDashboardProps> = ({ onOpenCatalog }) => {
  const [dashboard, setDashboard] = useState<ChannelOperationsDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const loadDashboard = useCallback(async (mode: 'initial' | 'poll' | 'manual' = 'initial') => {
    try {
      if (mode === 'manual') {
        setRefreshing(true);
      } else if (mode === 'initial') {
        setLoading(true);
      }
      const data = await fetchChannelOperationsDashboard();
      setDashboard(data);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '';
      setError(message || 'Failed to load channel operations dashboard');
    } finally {
      if (mode === 'manual') {
        setRefreshing(false);
      } else if (mode === 'initial') {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadDashboard('initial');

    // This component unmounts when Catalog Sync Status is selected, so these
    // timers never drive MtgjsonLoadStatus / TcgcsvCatalogStatus.
    const pollId = setInterval(() => {
      void loadDashboard('poll');
    }, OPERATIONS_POLL_MS);
    const ageId = setInterval(() => {
      setNowMs(Date.now());
    }, OPERATIONS_AGE_TICK_MS);

    return () => {
      clearInterval(pollId);
      clearInterval(ageId);
    };
  }, [loadDashboard]);

  if (loading && !dashboard) {
    return (
      <div style={styles.loadingContainer}>
        <Spinner size="lg" />
        <p style={styles.loadingText}>Loading channel operations...</p>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <Alert
        type={error ? 'error' : 'warning'}
        message={error || 'No dashboard data available'}
      />
    );
  }

  const environment = 'environment' in dashboard.environment ? dashboard.environment : null;
  const fanoutAlertStates = Array.isArray((dashboard.fanout as { alert_states?: unknown[] }).alert_states)
    ? ((dashboard.fanout as {
        alert_states?: Array<{
          metric_name: string;
          threshold_type?: string;
          state: string;
          current_value: number;
        }>;
      }).alert_states ?? []).filter((alert) => alert.threshold_type !== 'increase_1h')
    : [];
  const outboxBacklog = Array.isArray((dashboard.outbox as { channel_backlog?: unknown[] }).channel_backlog)
    ? ((dashboard.outbox as {
        channel_backlog?: Array<{
          channel: string;
          pending_count: number;
          processing_count: number;
          retry_alert_count: number;
          oldest_pending_age_seconds: number;
          oldest_processing_age_seconds?: number;
          stuck_processing_count?: number;
        }>;
      }).channel_backlog ?? [])
    : [];
  const stuckProcessingCount = (dashboard.outbox as { stuck_processing_count?: number }).stuck_processing_count ?? 0;
  const stuckProcessingAlertState =
    (dashboard.outbox as { stuck_processing_alert_state?: string }).stuck_processing_alert_state ??
    (stuckProcessingCount > 0 ? 'critical' : 'ok');
  const oldestProcessingAgeSeconds =
    (dashboard.outbox as { oldest_processing_age_seconds?: number }).oldest_processing_age_seconds ?? 0;
  const stuckProcessingJobs = Array.isArray((dashboard.outbox as { stuck_processing_jobs?: unknown[] }).stuck_processing_jobs)
    ? ((dashboard.outbox as {
        stuck_processing_jobs?: Array<{
          queue_id: number;
          channel: string;
          processing_age_seconds: number;
          last_error: string | null;
        }>;
      }).stuck_processing_jobs ?? [])
    : [];
  const channelHealthRows = Array.isArray((dashboard.channels as { channels?: unknown[] }).channels)
    ? ((dashboard.channels as { channels?: Array<{ channel: string; state: string; last_success_at: string | null; completed_attempts: number; failed_attempts: number }> }).channels ?? [])
    : [];
  const reconciliation = dashboard.reconciliation as {
    last_run?: { status?: string | null; synced_at?: string | null } | null;
    shipped_shortfall_with_stock?: { count?: number; alert_state?: string };
  };
  const freshness = dashboard.freshness as {
    mtgjson?: {
      last_run_at?: string | null;
      status?: string | null;
      tracked_version?: string | null;
    };
    pricing_snapshots_last_24h?: number;
  };
  const platformHealth = (dashboard.platform_health ?? {
    state: 'healthy',
    issues: [],
    vault: { ok: true },
    webhooks: { manapool_secret_configured: true },
  }) as {
    state?: 'healthy' | 'warning' | 'critical';
    issues?: string[];
    vault?: { ok?: boolean; message?: string };
    webhooks?: { manapool_secret_configured?: boolean };
  };
  const credentialFailureCount = (dashboard.outbox as { credential_failure_count?: number }).credential_failure_count ?? 0;
  const credentialFailures = Array.isArray((dashboard.outbox as { credential_failures?: unknown[] }).credential_failures)
    ? ((dashboard.outbox as {
        credential_failures?: Array<{ queue_id: number; channel: string; last_error: string }>;
      }).credential_failures ?? [])
    : [];
  const shippedShortfallWithStock = reconciliation.shipped_shortfall_with_stock?.count ?? 0;
  const oldestPendingAgeSeconds = (dashboard.outbox as { oldest_pending_age_seconds?: number }).oldest_pending_age_seconds ?? 0;
  const replayOperationsLast24h = (dashboard.outbox as { replay_operations_last_24h?: number }).replay_operations_last_24h ?? 0;
  const mtgjsonFreshness = freshness.mtgjson ?? {};
  const pricingSnapshotsLast24h = freshness.pricing_snapshots_last_24h ?? 0;

  return (
    <div>
      {error && <Alert type="error" message={error} />}

      {dashboard.dashboard_status === 'degraded' && (
        <Alert
          type="warning"
          message={`Dashboard is degraded. Check: ${dashboard.degraded_sections.join(', ')}`}
        />
      )}

      {(platformHealth.state === 'critical' || platformHealth.state === 'warning') && (
        <Alert
          type={platformHealth.state === 'critical' ? 'error' : 'warning'}
          message={
            platformHealth.issues?.length
              ? `Platform health ${platformHealth.state}: ${platformHealth.issues.join(' · ')}`
              : `Platform health is ${platformHealth.state}. Check Vault and channel credentials.`
          }
        />
      )}

      {credentialFailureCount > 0 && (
        <Alert
          type="error"
          message={`${credentialFailureCount} outbound dispatch job(s) failed due to missing credentials (often Vault sealed after restart). Re-queue from Channel Approvals after fixing Vault.`}
        />
      )}

      {((dashboard.outbox as { reclaim_pressure_count?: number }).reclaim_pressure_count ?? 0) > 0 && (
        <Alert
          type="error"
          message={`${(dashboard.outbox as { reclaim_pressure_count?: number }).reclaim_pressure_count} outbox job(s) show reclaim-loop pressure (reclaimed mid-flight repeatedly). Check dispatcher-worker heartbeats and DISPATCHER_STALE_PROCESSING_SECONDS.`}
        />
      )}

      {((dashboard.outbox as { permanent_partial_count_24h?: number }).permanent_partial_count_24h ?? 0) > 0 && (
        <Alert
          type="warning"
          message={`${(dashboard.outbox as { permanent_partial_count_24h?: number }).permanent_partial_count_24h} completed fan-out job(s) in the last 24h finished with permanent line failures (e.g. Product not found). Review sync_queue.last_error / permanent_errors.`}
        />
      )}

      <div style={styles.opsHeaderGrid}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Environment</h2>
            <div style={styles.headerActions}>
              <button
                type="button"
                onClick={() => {
                  void loadDashboard('manual');
                }}
                disabled={refreshing}
                style={{
                  ...styles.linkButton,
                  cursor: refreshing ? 'wait' : 'pointer',
                  fontFamily: 'inherit',
                  opacity: refreshing ? 0.7 : 1,
                }}
              >
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
              <span style={styles.healthBadge}>{environment?.environment?.toUpperCase() || 'UNKNOWN'}</span>
            </div>
          </div>
          <div style={styles.cardGrid}>
            <div>
              <p style={styles.statLabel}>Build SHA</p>
              <p style={styles.statValueSmall}>{environment?.build_sha || 'n/a'}</p>
            </div>
            <div>
              <p style={styles.statLabel}>API Version</p>
              <p style={styles.statValueSmall}>{environment?.api_version || 'n/a'}</p>
            </div>
            <div>
              <p style={styles.statLabel}>Generated</p>
              <p style={styles.statValueSmall}>{formatTimestamp(dashboard.generated_at, nowMs)}</p>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Risk Signals</h2>
          <div style={styles.opsMetricGrid}>
            <MetricTile label="Oldest queued event" value={formatDurationSeconds(oldestPendingAgeSeconds)} />
            <MetricTile label="Replay ops (24h)" value={String(replayOperationsLast24h)} />
            <MetricTile
              label="Shipped shortfall w/ stock"
              value={String(shippedShortfallWithStock)}
              tone={shippedShortfallWithStock > 0 ? 'error' : 'default'}
            />
            <MetricTile
              label="Vault"
              value={platformHealth.vault?.ok ? 'OK' : 'Issue'}
              tone={platformHealth.vault?.ok ? 'default' : 'error'}
            />
            <MetricTile
              label="Webhook secret"
              value={platformHealth.webhooks?.manapool_secret_configured ? 'OK' : 'Missing'}
              tone={platformHealth.webhooks?.manapool_secret_configured ? 'default' : 'error'}
            />
            <MetricTile
              label="Credential dispatch failures"
              value={String(credentialFailureCount)}
              tone={credentialFailureCount > 0 ? 'error' : 'default'}
            />
          </div>
          <div style={{ marginTop: spacing.md, display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
            <Link to="/reports/shipped-shortfall-with-stock" style={styles.linkButton}>
              Open shortfall report
            </Link>
            <Link to="/reports/fanout-slo-breaches" style={styles.linkButton}>
              Fanout SLO Breaches
            </Link>
            {credentialFailureCount > 0 && (
              <Link to="/channels/approvals" style={styles.linkButton}>
                Re-queue credential failures
              </Link>
            )}
          </div>
          {credentialFailures.length > 0 && (
            <div style={{ marginTop: spacing.md }}>
              <p style={styles.statLabel}>Recent credential dispatch failures</p>
              <div style={styles.keyValueList}>
                {credentialFailures.slice(0, 5).map((failure) => (
                  <div key={failure.queue_id} style={styles.keyValueRow}>
                    <span style={styles.serviceStatLabel}>
                      #{failure.queue_id} · {failure.channel}
                    </span>
                    <span style={styles.serviceStatValue}>{failure.last_error.slice(0, 80)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={styles.opsGrid}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Channel Health</h2>
            <Link to="/reports/sync-reliability" style={styles.linkButton}>Sync Reliability</Link>
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Channel</th>
                  <th style={styles.th}>State</th>
                  <th style={styles.th}>Last Success</th>
                  <th style={styles.th}>Completed</th>
                  <th style={styles.th}>Failed</th>
                </tr>
              </thead>
              <tbody>
                {channelHealthRows.map((channel) => (
                  <tr key={channel.channel}>
                    <td style={styles.td}>{channel.channel}</td>
                    <td style={styles.td}><StatusPill value={channel.state} /></td>
                    <td style={styles.td}>{formatTimestamp(channel.last_success_at)}</td>
                    <td style={styles.td}>{channel.completed_attempts}</td>
                    <td style={styles.td}>{channel.failed_attempts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Outbox Backlog</h2>
            <Link to="/reports/outbox-reliability" style={styles.linkButton}>Outbox Reliability</Link>
          </div>
          <div style={{ marginBottom: spacing.md, display: 'flex', gap: spacing.md, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={styles.inlineStatusGroup}>
              <StatusPill value={stuckProcessingAlertState} />
              <span style={styles.serviceStatValue}>
                Stuck processing: {stuckProcessingCount}
                {oldestProcessingAgeSeconds > 0
                  ? ` (oldest ${formatDurationSeconds(oldestProcessingAgeSeconds)})`
                  : ''}
              </span>
            </span>
          </div>
          {stuckProcessingJobs.length > 0 && (
            <div style={{ ...styles.keyValueList, marginBottom: spacing.md }}>
              {stuckProcessingJobs.slice(0, 5).map((job) => (
                <div key={job.queue_id} style={styles.keyValueRow}>
                  <span style={styles.serviceStatLabel}>
                    #{job.queue_id} {job.channel}
                  </span>
                  <span style={styles.serviceStatValue}>
                    {formatDurationSeconds(job.processing_age_seconds)}
                    {job.last_error ? ` — ${job.last_error}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Channel</th>
                  <th style={styles.th}>Pending</th>
                  <th style={styles.th}>Processing</th>
                  <th style={styles.th}>Stuck</th>
                  <th style={styles.th}>Retry Alerts</th>
                  <th style={styles.th}>Oldest Pending</th>
                  <th style={styles.th}>Oldest Processing</th>
                </tr>
              </thead>
              <tbody>
                {outboxBacklog.map((row) => (
                  <tr key={row.channel}>
                    <td style={styles.td}>{row.channel}</td>
                    <td style={styles.td}>{row.pending_count}</td>
                    <td style={styles.td}>{row.processing_count}</td>
                    <td style={styles.td}>{row.stuck_processing_count ?? 0}</td>
                    <td style={styles.td}>{row.retry_alert_count}</td>
                    <td style={styles.td}>{formatDurationSeconds(row.oldest_pending_age_seconds)}</td>
                    <td style={styles.td}>
                      {formatDurationSeconds(row.oldest_processing_age_seconds ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Inbound recon history</h2>
            <Link to="/reports/reconciliation" style={styles.linkButton}>
              Inbound recon history
            </Link>
          </div>
          <p style={{ ...styles.statValueSmall, color: colors.textSecondary, marginBottom: spacing.md }}>
            History of inbound Manapool reconciliation runs (daily/weekly drift, recurring mismatches,
            policy outcomes). For live SyncBase vs channel inventory confidence, use Channel Parity.
          </p>
          {reconciliation.last_run ? (
            <div style={styles.cardGrid}>
              <div>
                <p style={styles.statLabel}>Status</p>
                <p style={{
                  ...styles.statValueSmall,
                  color: reconciliation.last_run.status === 'success'
                    ? colors.success
                    : reconciliation.last_run.status === 'failed'
                      ? colors.error
                      : colors.textSecondary,
                }}>
                  {reconciliation.last_run.status || 'Unknown'}
                </p>
              </div>
              <div>
                <p style={styles.statLabel}>Last Run</p>
                <p style={styles.statValueSmall}>{formatTimestamp(reconciliation.last_run.synced_at ?? null)}</p>
              </div>
            </div>
          ) : (
            <p style={{...styles.statValueSmall, color: colors.textSecondary}}>No inbound reconciliation records found.</p>
          )}
          <div style={{ marginTop: spacing.md }}>
            <Link to="/reports/channel-parity" style={styles.linkButton}>Open Channel Parity</Link>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Fanout Alerts</h2>
            <Link to="/reports/fanout-slo-breaches" style={styles.linkButton}>Fanout SLO Breaches</Link>
          </div>
          <p style={{ ...styles.statValueSmall, color: colors.textSecondary, marginBottom: spacing.md }}>
            Stuck processing and reclaim come from the outbox. Hourly counter history is on Fanout SLO Breaches.
          </p>
          {fanoutAlertStates.length > 0 ? (
            <div style={styles.keyValueList}>
              {fanoutAlertStates.map((alert) => (
                <div key={alert.metric_name} style={styles.keyValueRow}>
                  <span style={styles.serviceStatLabel}>{formatMetricName(alert.metric_name)}</span>
                  <span style={styles.inlineStatusGroup}>
                    <StatusPill value={alert.state} />
                    <span style={styles.serviceStatValue}>{formatMetricValue(alert.current_value, alert.metric_name)}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{...styles.statValueSmall, color: colors.textSecondary}}>No alerts configured</p>
          )}
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>Data Freshness</h2>
            <button
              type="button"
              onClick={onOpenCatalog}
              style={{ ...styles.linkButton, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              View catalog sync
            </button>
          </div>
          <div style={styles.cardGrid}>
            <div>
              <p style={styles.statLabel}>MTGJSON Last Run</p>
              <p style={styles.statValueSmall}>{formatTimestamp(mtgjsonFreshness.last_run_at ?? null)}</p>
            </div>
            <div>
              <p style={styles.statLabel}>MTGJSON Status</p>
              <p style={{
                ...styles.statValueSmall, 
                color: mtgjsonFreshness.status === 'success' ? colors.success : mtgjsonFreshness.status === 'failed' ? colors.error : colors.textSecondary
              }}>
                {mtgjsonFreshness.status ? mtgjsonFreshness.status.toUpperCase() : 'Unknown'}
              </p>
            </div>
            <div>
              <p style={styles.statLabel}>Tracked Version</p>
              <p style={styles.statValueSmall}>{mtgjsonFreshness.tracked_version || 'Not set'}</p>
            </div>
            <div>
              <p style={styles.statLabel}>Pricing Snapshots (24h)</p>
              <p style={styles.statValueSmall}>{pricingSnapshotsLast24h}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SyncStatusDashboard: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const loadSyncStatus = async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const data = await fetchSyncStatus();
      setSyncStatus(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load sync status');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadSyncStatus();
    
    // Poll status in the background without triggering page-level loading UI.
    const interval = setInterval(() => {
      loadSyncStatus({ silent: true });
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleManualSync = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      await triggerMTGJSONSync();
      setSyncMessage('Sync accepted. Waiting for the first status update from the backend.');
      setTimeout(() => loadSyncStatus({ silent: true }), 10000);
    } catch (err: any) {
      setSyncMessage(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const formatTimestamp = (timestamp: string | null): string =>
    formatRelativeTime(timestamp, { fallback: 'Never' });

  const getHealthColor = (health: string): string => {
    switch (health) {
      case 'healthy': return colors.success;
      case 'warning': return colors.warning;
      case 'error': return colors.error;
      default: return colors.textSecondary;
    }
  };

  const getStatusBadgeColor = (status: string): React.CSSProperties => {
    switch (status) {
      case 'running':
        return { backgroundColor: colors.primary + '20', color: colors.primary };
      case 'timed_out':
        return { backgroundColor: colors.error + '20', color: colors.error };
      case 'success':
        return { backgroundColor: colors.success + '20', color: colors.success };
      case 'warning':
        return { backgroundColor: colors.warning + '20', color: colors.warning };
      case 'failed':
        return { backgroundColor: colors.error + '20', color: colors.error };
      default:
        return { backgroundColor: colors.gray200, color: colors.textSecondary };
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <Spinner size="lg" />
        <p style={styles.loadingText}>Loading sync status...</p>
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  if (!syncStatus) {
    return <Alert type="warning" message="No sync status available" />;
  }

  const serviceOrder = [
    'sync_orchestrator',
    'mtgjson_meta',
    'mtgjson_sets',
    'mtgjson_supplementary',
    'mtgjson_pricing',
    'mtgjson_decks',
    'mtgjson_psql',
    'tcgplayer_skus',
    'tcgcsv_pricing',
    'tcgcsv_catalog',
  ];
  const sortedServices = Object.entries(syncStatus.services)
    .sort(([a], [b]) => {
      const indexA = serviceOrder.indexOf(a);
      const indexB = serviceOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

  return (
    <div>
      <div style={{
        ...styles.card,
        borderLeft: `4px solid ${getHealthColor(syncStatus.overall_health)}`
      }}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>Catalog Health</h2>
          <span style={{
            ...styles.healthBadge,
            backgroundColor: getHealthColor(syncStatus.overall_health) + '20',
            color: getHealthColor(syncStatus.overall_health)
          }}>
            {syncStatus.overall_health.toUpperCase()}
          </span>
        </div>
        <div style={styles.cardGrid}>
          <div>
            <p style={styles.statLabel}>Last Successful Sync</p>
            <p style={styles.statValue}>{formatTimestamp(syncStatus.last_successful_sync)}</p>
          </div>
          <div>
            <p style={styles.statLabel}>Active Services</p>
            <p style={styles.statValue}>{Object.keys(syncStatus.services).length}</p>
          </div>
        </div>
        <div style={{ marginTop: spacing.md }}>
          <Button
            onClick={handleManualSync}
            disabled={syncing}
            variant="primary"
          >
            {syncing ? 'Syncing...' : '🔄 Trigger Manual Sync'}
          </Button>
          {syncMessage && (
            <p style={{
              ...styles.syncMessage,
              color: syncMessage.includes('failed') ? colors.error : colors.success
            }}>
              {syncMessage}
            </p>
          )}
        </div>
      </div>

      {/* Catalog load status: MTGJSON (Magic) and TCGCSV (Pokémon + Lorcana) are separate. */}
      <MtgjsonLoadStatus />
      <TcgcsvCatalogStatus />

      {/* Service Status Cards */}
      <div style={styles.servicesGrid}>
        {sortedServices.map(([serviceName, service]) => (
          <ServiceCard
            key={serviceName}
            serviceName={serviceName}
            service={service}
            formatTimestamp={formatTimestamp}
            getStatusBadgeColor={getStatusBadgeColor}
          />
        ))}
      </div>
    </div>
  );
};

interface ServiceCardProps {
  serviceName: string;
  service: SyncServiceStatus;
  formatTimestamp: (timestamp: string | null) => string;
  getStatusBadgeColor: (status: string) => React.CSSProperties;
}

const TERMINAL_SERVICE_PHASES = new Set([
  'completed',
  'failed',
  'import_failed',
  'timed_out',
  'validation_failed',
  'preferred_price_refresh_failed',
]);

function getDisplayedServiceStatus(service: SyncServiceStatus): string {
  const phase = typeof service.details?.phase === 'string' ? service.details.phase : null;

  if (phase === 'timed_out') {
    return 'timed_out';
  }

  if (service.status === 'warning' && phase && !TERMINAL_SERVICE_PHASES.has(phase)) {
    return 'running';
  }

  return service.status;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  serviceName,
  service,
  formatTimestamp,
  getStatusBadgeColor
}) => {
  const displayStatus = getDisplayedServiceStatus(service);
  const phase = typeof service.details?.phase === 'string' ? service.details.phase : null;
  const currentActivity = typeof service.details?.current_activity === 'string' ? service.details.current_activity : null;
  const activityUpdatedAt = typeof service.details?.activity_updated_at === 'string' ? service.details.activity_updated_at : null;
  const timedOutAfterSeconds = typeof service.details?.timed_out_after_seconds === 'number'
    ? service.details.timed_out_after_seconds
    : null;
  const serviceLabels: Record<string, string> = {
    'sync_orchestrator': 'Sync Orchestrator',
    'mtgjson_meta': 'MTGJSON Metadata',
    'mtgjson_sets': 'Card/Set Data',
    'mtgjson_supplementary': 'Supplementary Data',
    'mtgjson_pricing': 'Pricing Data',
    'mtgjson_decks': 'Deck Data',
    'mtgjson_psql': 'Card/Set Data (Legacy)',
    'tcgplayer_skus': 'TCGPlayer SKUs (Legacy)',
    'tcgcsv_pricing': 'Pricing Data (Legacy)'
  };

  return (
    <div style={styles.serviceCard}>
      <div style={styles.serviceHeader}>
        <h3 style={styles.serviceTitle}>{serviceLabels[serviceName] || serviceName}</h3>
        <span style={{
          ...styles.statusBadge,
          ...getStatusBadgeColor(displayStatus)
        }}>
          {displayStatus}
        </span>
      </div>
      <div style={styles.serviceStats}>
        <div style={styles.serviceStat}>
          <span style={styles.serviceStatLabel}>Last Run:</span>
          <span style={styles.serviceStatValue}>{formatTimestamp(service.last_run_at)}</span>
        </div>
        {phase && (
          <div style={styles.serviceStat}>
            <span style={styles.serviceStatLabel}>Phase:</span>
            <span style={styles.serviceStatValue}>{phase}</span>
          </div>
        )}
        <div style={styles.serviceStat}>
          <span style={styles.serviceStatLabel}>Records:</span>
          <span style={styles.serviceStatValue}>{service.records_processed.toLocaleString()}</span>
        </div>
        <div style={styles.serviceStat}>
          <span style={styles.serviceStatLabel}>Errors:</span>
          <span style={{
            ...styles.serviceStatValue,
            color: service.errors > 0 ? colors.error : colors.success
          }}>
            {service.errors}
          </span>
        </div>
        {service.failure_rate > 0 && (
          <div style={styles.serviceStat}>
            <span style={styles.serviceStatLabel}>Failure Rate:</span>
            <span style={styles.serviceStatValue}>{service.failure_rate.toFixed(2)}%</span>
          </div>
        )}
        {timedOutAfterSeconds !== null && (
          <div style={styles.serviceStat}>
            <span style={styles.serviceStatLabel}>Timeout:</span>
            <span style={{ ...styles.serviceStatValue, color: colors.error }}>{timedOutAfterSeconds}s</span>
          </div>
        )}
      </div>
      {(currentActivity || activityUpdatedAt) && (
        <div style={styles.serviceStats}>
          {currentActivity && (
            <div style={styles.serviceStat}>
              <span style={styles.serviceStatLabel}>Activity:</span>
              <span style={styles.serviceStatValue}>{currentActivity}</span>
            </div>
          )}
          {activityUpdatedAt && (
            <div style={styles.serviceStat}>
              <span style={styles.serviceStatLabel}>Heartbeat:</span>
              <span style={styles.serviceStatValue}>{formatTimestamp(activityUpdatedAt)}</span>
            </div>
          )}
        </div>
      )}
      {service.details && Object.keys(service.details).length > 0 && (
        <details style={styles.serviceDetails}>
          <summary style={styles.serviceDetailsSummary}>Details</summary>
          <pre style={styles.serviceDetailsContent}>
            {JSON.stringify(service.details, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

const MetricTile: React.FC<{ label: string; value: string; tone?: 'default' | 'error' }> = ({ label, value, tone = 'default' }) => (
  <div style={styles.metricTile}>
    <div style={{...styles.statLabel, minHeight: '2.4em', display: 'flex', alignItems: 'flex-start'}}>{label}</div>
    <div style={{ ...styles.statValue, color: tone === 'error' ? colors.error : colors.text, fontSize: typographyFlat.fontSize.lg }}>{value}</div>
  </div>
);

const StatusPill: React.FC<{ value: string }> = ({ value }) => {
  const normalized = value.toLowerCase();
  let backgroundColor = colors.gray200;
  let color = colors.textSecondary;

  if (normalized === 'healthy' || normalized === 'success' || normalized === 'completed') {
    backgroundColor = `${colors.success}20`;
    color = colors.success;
  } else if (normalized === 'warning') {
    backgroundColor = `${colors.warning}20`;
    color = colors.warning;
  } else if (normalized === 'high' || normalized === 'critical' || normalized === 'failed' || normalized === 'degraded') {
    backgroundColor = `${colors.error}20`;
    color = colors.error;
  }

  return <span style={{ ...styles.statusBadge, backgroundColor, color }}>{value}</span>;
};

function formatDurationSeconds(value: number | null | undefined): string {
  if (!value) return '0s';
  const seconds = Math.max(0, Math.floor(value));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function formatTimestamp(timestamp: string | null, nowMs?: number): string {
  return formatRelativeTime(timestamp, { fallback: 'Never', maxDays: 1, nowMs });
}

function formatMetricName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatMetricValue(value: number, metricName: string): string {
  if (metricName.includes('latency') || metricName.includes('lag')) {
    return `${value.toFixed(0)}ms`;
  }
  if (metricName.includes('total') || metricName.includes('count')) {
    return `${value.toFixed(0)}`;
  }
  return value.toFixed(2);
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.xl,
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: spacing.lg,
  },
  title: {
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
    margin: 0,
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
    margin: 0,
  },
  tabs: {
    display: 'flex',
    gap: spacing.sm,
    borderBottom: `2px solid ${colors.border}`,
    marginBottom: spacing.xl,
  },
  tab: {
    padding: `${spacing.md} ${spacing.lg}`,
    background: 'none',
    border: 'none',
    borderBottom: '2px solid transparent',
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '-2px',
  },
  tabActive: {
    color: colors.primary,
    borderBottomColor: colors.primary,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  content: {
    minHeight: '400px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.xl,
  },
  loadingText: {
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.lg,
    border: `1px solid ${colors.border}`,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  },
  linkButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    color: colors.primary,
    textDecoration: 'none',
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  cardTitle: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
    margin: 0,
  },
  healthBadge: {
    padding: `${spacing.xs} ${spacing.md}`,
    borderRadius: borderRadius.full,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.bold,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.lg,
    marginBottom: spacing.md,
  },
  opsHeaderGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: spacing.lg,
    marginBottom: spacing.lg,
  },
  opsMetricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: spacing.md,
    marginTop: spacing.md,
  },
  opsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
    gap: spacing.lg,
  },
  statLabel: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    margin: 0,
    marginBottom: spacing.xs,
  },
  statValue: {
    fontSize: typographyFlat.fontSize.xl,
    fontWeight: typographyFlat.fontWeight.bold,
    color: colors.text,
    margin: 0,
  },
  statValueSmall: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    margin: 0,
  },
  metricTile: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.background,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '120px',
    justifyContent: 'space-between',
  },
  syncMessage: {
    marginTop: spacing.sm,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.md} ${spacing.sm}`,
    borderBottom: `2px solid ${colors.border}`,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing.md} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typography.body.fontSize,
    verticalAlign: 'middle',
  },
  keyValueList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  keyValueRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
  },
  inlineStatusGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
  },
  servicesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: spacing.lg,
  },
  serviceCard: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
  },
  serviceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
    paddingBottom: spacing.sm,
    borderBottom: `1px solid ${colors.border}`,
  },
  serviceTitle: {
    fontSize: typography.h3.fontSize,
    fontWeight: typography.h3.fontWeight,
    color: colors.text,
    margin: 0,
  },
  statusBadge: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
    fontSize: typography.caption.fontSize,
    fontWeight: typographyFlat.fontWeight.bold,
    textTransform: 'uppercase',
  },
  serviceStats: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  serviceStat: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  serviceStatLabel: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  serviceStatValue: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  serviceDetails: {
    marginTop: spacing.md,
    padding: spacing.sm,
    backgroundColor: colors.background,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.border}`,
  },
  serviceDetailsSummary: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    cursor: 'pointer',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  serviceDetailsContent: {
    marginTop: spacing.sm,
    fontSize: typography.caption.fontSize,
    color: colors.text,
    overflow: 'auto',
    maxHeight: '200px',
  },
  placeholder: {
    textAlign: 'center',
    padding: spacing['3xl'],
  },
  placeholderIcon: {
    fontSize: '64px',
    marginBottom: spacing.lg,
  },
  placeholderTitle: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
    marginBottom: spacing.md,
  },
  placeholderText: {
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
    marginBottom: spacing.lg,
  },
  placeholderList: {
    listStyle: 'none',
    padding: 0,
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
    display: 'inline-block',
    textAlign: 'left',
  },
};
